import { useTranslation } from 'next-i18next/pages';
import Image from 'next/image';
import React from 'react';

import useCurrentLang from '@/hooks/apps/useCurrentLang';

import Area from '@/components/apps/layouts/shared/Area';
import SectionTitle from '@/components/apps/ui/title/SectionTitle';
import { AniSlideUp } from '@/components/shared/animations/basic';

import {
  useSafetyMonitoringDashboard,
  useSafetyMonitoringFeatures,
} from '../useSafetyPageData';

const DASHBOARD_IMAGE_SIZES =
  '(min-width: 1540px) 1480px, (min-width: 992px) calc(100vw - 80px), calc(100vw - 40px)';

/**
 * ### SystemSection
 *
 * The monitoring system laid out like the Industrial page's: the live
 * dashboard capture, then the capabilities as one divided list with the
 * name in the left column and the sentence in the right.
 *
 * @returns The rendered monitoring section element
 * @see {@link useSafetyMonitoringDashboard} The capture shown
 * @see {@link useSafetyMonitoringFeatures} The list rendered under it
 */
export default function SystemSection() {
  const { t } = useTranslation('common');
  const lang = useCurrentLang();
  const dashboard = useSafetyMonitoringDashboard(t, lang);
  const features = useSafetyMonitoringFeatures(t);

  return (
    // 배경은 image-set() + lg 미디어쿼리라 유틸리티로 표현할 수 없어 globals.css 에 둔다.
    <section
      className="safety-system-bg"
      data-analytics-section="monitoring_system"
    >
      <Area>
        <SectionTitle
          color="lighter"
          title={t('모니터링 시스템')}
          description={t(
            'Listen AI 안전 솔루션의 사고 감지 상황을 실시간으로 확인할 수 있는 모니터링 시스템 서비스',
          )}
        />
        <div className="mt-[40px] overflow-hidden rounded-[20px] border border-[rgba(255,255,255,0.1)] lg:mt-[clamp(40px,6.667vw,80px)] lg:rounded-[clamp(20px,2.222vw,32px)]">
          <Image
            src={dashboard.image.src}
            width={dashboard.image.width}
            height={dashboard.image.height}
            alt={dashboard.alt}
            sizes={DASHBOARD_IMAGE_SIZES}
            quality={86}
            className="block h-auto w-full"
          />
        </div>

        <AniSlideUp>
          <ul className="pt-[48px]">
            {features.map((feature) => (
              <li
                key={feature.title}
                className="px-0 py-[24px] not-last:border-b not-last:border-b-apps-brandGray50 lg:grid lg:grid-cols-[calc(484/1480*100%)_calc(976/1480*100%)] lg:gap-[20px] lg:py-[clamp(24px,3.333vw,40px)]"
              >
                <h3 className="text-[22px] font-bold leading-[160%] tracking-[-0.44px] lg:text-[clamp(22px,2.333vw,28px)] lg:leading-[140%] lg:tracking-[-0.56px]">
                  {feature.title}
                </h3>
                <p className="mt-[4px] text-[16px] font-medium leading-[140%] tracking-[-0.32px] text-apps-brandGray600 lg:text-[18px] lg:leading-[160%] lg:tracking-[-0.36px]">
                  {feature.description}
                </p>
              </li>
            ))}
          </ul>
        </AniSlideUp>
      </Area>
    </section>
  );
}
